<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<template>
  <li @keyup.tab="isFocused && (showToggle = true)">
    <NavBarDropdown
      v-if="children.length"
      :id
      :onShow="trackHover"
      @focus="isFocused = true"
      @blur="onBlur"
    >
      <template #trigger="{ isOpen, isMenuActive, showMenu, hideMenu }">
        <div class="flex h-full gap-4">
          <RevLink
            :aria-controls="`submenu-${id}`"
            :aria-expanded="isOpen"
            aria-haspopup="true"
            class="text-action-default-hi h-full text-left transition-all duration-700 ease-in-out hover:no-underline"
            :class="[
              isCompactNavEnabled
                ? 'caption lg:body-2 hover:caption-bold lg:hover:body-2-bold'
                : 'body-2 hover:body-2-bold',
              {
                'caption-bold lg:body-2-bold':
                  isCompactNavEnabled && isLinkActive,
                'body-2-bold': !isCompactNavEnabled && isLinkActive,
                'text-action-default-low': isMenuActive,
              },
            ]"
            role="menuitem"
            :to="url"
            :tracking="{
              zone: 'header',
              name: 'department',
              value: title,
            }"
            :underlined="false"
            @click.capture="
              (event: Event) => {
                if (!isHoverMedia && !isOpen) event.preventDefault()
                else hideMenu()
              }
            "
          >
            <div
              class="relative flex h-full items-center text-nowrap after:bg-action-default-hi after:pointer-events-none after:absolute after:-bottom-[1px] after:left-0 after:h-[2px] after:w-full after:opacity-0 after:content-[''] hover:after:opacity-100"
              :class="{
                'gap-4 lg:gap-6': isCompactNavEnabled,
                'after:opacity-100': isLinkActive,
              }"
            >
              <component
                :is="resolveNavIcon(icon)"
                v-if="icon"
                :size="isCompactNavEnabled ? '20' : undefined"
              />
              <BoldHack :text="title" :compact="isCompactNavEnabled" />
            </div>
          </RevLink>
          <RevLink
            v-show="showToggle"
            :aria-controls="`submenu-${id}`"
            :aria-expanded="isOpen"
            aria-haspopup="true"
            :aria-label="
              isOpen
                ? i18n(translations.hideSubmenu, { title: title })
                : i18n(translations.showSubmenu, { title: title })
            "
            class="flex h-full items-center"
            @click="isOpen ? hideMenu() : showMenu()"
            ><IconChevronDown
          /></RevLink>
        </div>
      </template>

      <template #panel="{ hideMenu }">
        <div class="flex">
          <NavServiceList
            v-for="service in details"
            :key="service.name"
            class="bg-surface-default-mid max-w-256"
            :service
          />
          <NavMenuContent
            class="flex-1"
            :items="children"
            :universeTitle="title"
            :universeURL="url"
            @click-item="hideMenu"
          />
        </div>
      </template>
    </NavBarDropdown>
    <NavBarLink
      v-else
      :active="isActivePage(route, url)"
      :color
      :compact="isCompactNavEnabled"
      :icon="icon ? resolveNavIcon(icon) : undefined"
      :label="title"
      :to="url"
      :tracking="{
        zone: 'header',
        name: 'department',
        value: title,
      }"
    />
  </li>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevLink } from '@ds/components/Link'
import { IconChevronDown } from '@ds/icons/IconChevronDown'
import { useMediaQuery } from '@vueuse/core'

import { useActiveMenu } from '../../composables/useActiveMenu'
import type { NavigationItem } from '../../types'
import { isActivePage } from '../../utils/isActivePage'
import { resolveNavIcon } from '../../utils/navigationIcons'
import NavServiceList from '../NavBurgerMenu/NavServiceList.vue'

import BoldHack from './BoldHack.vue'
import NavBarDropdown from './NavBarDropdown.vue'
import NavBarLink from './NavBarLink.vue'
import translations from './NavMenu.translations'
import NavMenuContent from './NavMenuContent.vue'

const props = defineProps<NavigationItem & { position: number }>()

const showToggle = ref(false)
const isFocused = ref(false)

const i18n = useI18n()
const isHoverMedia = useMediaQuery('(hover: hover)')
const route = useRoute()
const { trackHoverOver } = useTracking()
const router = useRouter()

const experiments = useExperiments()
const isCompactNavEnabled = computed(
  () => experiments['experiment.desktopNavStyling'] === 'compact',
)
const { isMenuActive: isAnyMenuActive } = useActiveMenu()
const isLinkActive = computed(
  () => isActivePage(route, props.url) && !isAnyMenuActive.value,
)

function trackHover() {
  trackHoverOver({
    zone: 'header',
    name: 'department',
    value: props.title,
    page_type:
      router.currentRoute.value.name?.toString() ??
      router.currentRoute.value.fullPath,
    position: props.position,
  })
}

function onBlur() {
  isFocused.value = false
  showToggle.value = false
}
</script>
